import type { ContractLifecycleView } from "@closer/shared";
import { CONTRACT_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, PageHeader, Popover, Skeleton, Tabs, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import {
  activeContractDetailSection,
  CONTRACT_DETAIL_SECTIONS,
  contractDetailSectionPath,
} from "../../lib/contractDetailSections";
import { contractStatusTone } from "../../lib/contractStatusTone";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ContractAccessDialog } from "./ContractAccessDialog";
import { ContractArchiveDialog } from "./ContractArchiveDialog";
import type { ContractDetailContext, ContractDetailRecord } from "./contractDetailContext";
import { ContractLifecyclePill } from "./ContractLifecycleStrip";
import { ContractRestoreDialog } from "./ContractRestoreDialog";

/**
 * Sözleşme detay KABUĞU (CLOS-117 `[085]`).
 *
 * ### ⚠ Her bölüm KENDİ kanonik kaynağından
 *
 * Kart: *"her bölüm kendi kanonik kaynağından okunur; ekran veriyi İKİNCİ BİR SÖZLEŞME
 * KAYDINA kopyalamaz."* Tek birleşik bir uç yazsaydık, taraf listesindeki geçici bir arıza
 * sözleşmenin tamamını kapatır ve her yeni bölüm o ucu büyütürdü. Bölümler bağımsız
 * yükleniyor, bağımsız düşüyor.
 *
 * ### ⚠ Ama AYNI erişim kararı altında
 *
 * Ana kayıt düşerse hiçbir bölüm çizilmiyor: kabuk tek bir `StateView` gösteriyor. Bu
 * yüzden künye ve türetilen kararlar (`kayitYazma`, `arsivde`) burada BİR KEZ hesaplanıp
 * bölümlere outlet bağlamıyla iniyor (`contractDetailContext.ts`).
 *
 * ### ⚠ Bölümler AYRI ROTALAR
 *
 * Bu dosya eskiden 1239 satırlık TEK bir sayfaydı ve dört bölümü `hidden` sınıfıyla aynı
 * anda DOM'da tutuyordu. Bölüm artık rota: adres bölümü söylüyor, geri tuşu çalışıyor,
 * yalnız açık bölümün sorguları koşuyor. Kayıt `lib/contractDetailSections.ts`'te —
 * `ContractsLayout` ve `OrganizationLayout` ile aynı desen.
 */
export function ContractDetailLayout() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * ⚠ Listeye dönüş geliş bağlamını taşıyor (kart: *"güvenli geri dönüşte KORUNABİLEN
   * liste/filtre durumu"*). Bağlam adreste, bileşen state'inde değil: state yenilemede ve
   * paylaşılan bağlantıda kaybolurdu.
   */
  const listeSorgusu = params.get("liste") ?? "";
  const listeYolu = `/app/sozlesmeler${listeSorgusu}`;

  const anahtar = (bolum: string) => tenantKey(tenantId, "contract", id, bolum);
  const getir =
    <T,>(yol: string) =>
    async () =>
      (await api<T>(yol, { tenantId })).data;

  const detay = useQuery({
    queryKey: anahtar("detay"),
    queryFn: getir<ContractDetailRecord>(`/api/contracts/${id}`),
  });

  const yasam = useQuery({
    queryKey: anahtar("yasam"),
    queryFn: getir<ContractLifecycleView>(`/api/contracts/${id}/lifecycle`),
    enabled: detay.isSuccess,
  });

  /**
   * ⚠ Geçişten sonra BÜTÜN bölümler yenileniyor. Yalnız yaşam döngüsü yenilenseydi
   * başlıktaki durum rozeti eski kalır, ekran kendi kendisiyle çelişirdi — kartın *"bölümler
   * tutarlı yenilenir"* kriteri.
   */
  /** Arşivleme onay penceresi (CLOS-122 `[104]`). */
  const [arsivAcik, setArsivAcik] = useState(false);
  /** Paylaşım penceresi (CLOS-144 `[125]`). */
  const [paylasimAcik, setPaylasimAcik] = useState(false);
  /** Arşivden geri alma onay penceresi (CLOS-123 `[105]`). */
  const [geriAlAcik, setGeriAlAcik] = useState(false);
  /** Başlıktaki `⋯` menüsü — ileri adım dışındaki durum geçişleri. */
  const [menuAcik, setMenuAcik] = useState(false);

  const gecis = useMutation({
    mutationFn: async (girdi: { to: string; reason?: string; acknowledgeWarnings?: boolean }) =>
      api(`/api/contracts/${id}/lifecycle/transitions`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id) });
      /** Liste de bayatladı: durum değişti, kayıt artık başka bir süzgece düşüyor olabilir. */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contracts") });
      setArsivAcik(false);
      setGeriAlAcik(false);
    },
    onError: (hata) => toast.error(resolveErrorState(hata).description),
  });

  /**
   * GERİ ALMA komutu (CLOS-123 `[105]`). Ayrı bir mutation, çünkü ayrı bir uç: hedefi
   * istemci değil GEÇMİŞ söylüyor.
   */
  const geriAl = useMutation({
    mutationFn: async (girdi: { reason?: string; acknowledgeWarnings: boolean }) =>
      api(`/api/contracts/${id}/lifecycle/restore`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id) });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contracts") });
      setGeriAlAcik(false);
    },
    onError: (hata) => toast.error(resolveErrorState(hata).description),
  });

  if (detay.isPending) {
    return (
      <div role="status" aria-label="Sözleşme yükleniyor">
        <Skeleton lines={8} />
      </div>
    );
  }

  /**
   * ⚠ ANA KAYIT düşerse hiçbir bölüm çizilmiyor ve 403 ile 404 AYNI metni gösteriyor
   * (`existenceIsSecret` varsayılanı). Farklı gösterseydi, elinde kimlik olan biri "bu
   * sözleşme var ama göremiyorum" bilgisini toplayabilirdi — `[079]`'un 404 kararının ekran
   * tarafındaki karşılığı.
   */
  if (detay.error || !detay.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(detay.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Kayıt bulunamadı",
                description: "Bu sözleşme yok ya da erişiminiz bulunmuyor.",
              },
              notFound: {
                title: "Kayıt bulunamadı",
                description: "Bu sözleşme yok ya da erişiminiz bulunmuyor.",
              },
              forbidden: {
                title: "Kayıt bulunamadı",
                description: "Bu sözleşme yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void detay.refetch()}
        />
        <Link className="text-sm font-medium text-primary hover:underline" to={listeYolu}>
          Listeye dön
        </Link>
      </div>
    );
  }

  const s = detay.data;
  /** Arşivdeki kayıtta yeni akış başlatılamıyor; sunucunun kuralı ekrana taşınıyor. */
  const arsivde = s.status === "archived";

  /**
   * KAYIT DÜZEYİ YAZMA (CLOS-888 son dilim).
   *
   * ⚠ Rol yetkisi TEK BAŞINA yetmiyor: 227 `standard_user` rolünün 227'si `contract.edit`
   * taşıyor (ölçüldü), yani kayda yalnız `read` erişimi verilen üye bugüne kadar bütün
   * yazma yüzeylerini görüyordu. Sunucu her zaman fail-closed'du — sızan şey yetki değil
   * DÜRÜSTLÜKTÜ: ürün yapabileceğini söyleyip yapamıyordu.
   *
   * ⚠ Alan YOKSA yazabilir sayılıyor. "Bilinmiyorsa kapat" daha güvenli görünüyor ama
   * burada yanlış: kapı zaten sunucuda ve fail-closed; alanı düşüren eski bir yanıt ya da
   * ara katman, ekranı kullanılamaz hâle getirirdi. Riskin yönü asimetrik — gereksiz
   * gösterilen düğme 404 verir, gereksiz gizlenen düğme işi tamamen durdurur.
   */
  const kayitYazma = s.viewerAccessLevel !== "read";

  /**
   * SONRAKİ ADIM — kanonik sıradaki durum, uygulanabilir geçişler arasında varsa.
   *
   * ⚠ Sıra sunucudan (`states`); ekranda ikinci bir akış tanımı yok. Sunucu ileri geçişi
   * sunmuyorsa (arşivde, terminal durumda, ya da yetki yoksa) birincil düğme çizilmiyor.
   */
  const gecisler = kayitYazma ? (yasam.data?.availableTransitions ?? []) : [];
  const durumlar = yasam.data?.states ?? [];
  const suAnkiSira = durumlar.findIndex((d) => d.code === yasam.data?.status);
  const sonrakiKod = suAnkiSira >= 0 ? durumlar[suAnkiSira + 1]?.code : undefined;
  const ileriGecis = sonrakiKod ? gecisler.find((g) => g.to === sonrakiKod) : undefined;
  const menuGecisleri = gecisler.filter((g) => g.to !== ileriGecis?.to);

  const bolumler = CONTRACT_DETAIL_SECTIONS;
  const aktifBolum = activeContractDetailSection(location.pathname, id);

  /** Bölümlere inen ORTAK karar kümesi — tek kaynak, `contractDetailContext.ts`. */
  const bolumBaglami: ContractDetailContext = {
    id,
    s,
    kayitYazma,
    arsivde,
    listeSorgusu,
    listeYolu,
  };

  return (
    /**
     * ⚠ RİTİM `gap-6` DEĞİL `gap-4` — ölçülen kalan farkın kaynağı buydu.
     *
     * Kabuk üç blok taşıyor (başlık · sekme şeridi · içerik) ve aralarındaki 24px'lik
     * boşluk, hedef tasarımın aynı yerdeki 16px'inden fazlaydı. Sayfanın kendi kabında
     * daraltılıyor, `PageHeader`'ın kendi iç ritmine dokunulmuyor — o bileşen bütün
     * ekranlarda ortak ve tek bir sayfa için değiştirilemez.
     */
    <div className="flex flex-col gap-4">
      {/*
        ⚠ Kritik bilgi ve aksiyonlar KOŞULSUZ görünür: kart *"mobilde kritik bilgi ve
        aksiyonlar kaybolmaz"* diyor. Başlığı dar ekranda gizleyen bir sınıf, kullanıcıyı
        hangi sözleşmede olduğunu bilmeden bırakırdı; bu yüzden burada `hidden`/`md:` yok,
        yalnız sarma (`flex-wrap`) var.
      */}
      {/*
        ⚠ Kırıntı yolu DEĞİL, tek bir geri bağlantısı (`PageHeader`'ın `breadcrumb`
        yuvasında taşınıyor).
        Onaylı tasarımda başlığın üstünde "Sözleşmeler / <sözleşme adı>" yazıyor. İkinci
        öğe başlığın kendisi ve hemen altında zaten aynı metin duruyor: aynı bilgiyi iki
        kez göstermek yalnız gereksiz değil, ÖLÇÜLÜR bir zarar — bir bekçi bölümleri
        `getByText(başlık)` ile sayıyor ve iki düğüm bulunca "hangisi?" diye kırılıyor.
        Geri dönüş bağlamı (`listeSorgusu`) korunuyor: kullanıcı gelinen daraltılmış
        listeye dönüyor.
      */}
      <PageHeader
        title={s.title}
        breadcrumb={
          <Link
            to={listeYolu}
            className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-soft transition-colors hover:text-primary"
          >
            <span aria-hidden="true">←</span> Sözleşmeler
          </Link>
        }
        meta={
          <>
            <span data-testid="sozlesme-durumu">
              {/* Ton ORTAK EŞLEMEDEN — liste ekranıyla aynı renk (`lib/contractStatusTone`). */}
              <Badge tone={contractStatusTone(s.status)}>
                {CONTRACT_STATUS_NAMES[s.status as keyof typeof CONTRACT_STATUS_NAMES] ?? s.status}
              </Badge>
            </span>
            {/*
              KAYIT NUMARASI ROZETİN YANINDA — konsept karşılaştırmasında ölçüldü (5 Eylül).

              ⚠ Kullanıcının sözleşmeye dışarıdan (e-posta, dilekçe, muhasebe) atıfta
              bulunurken kullandığı tek tutamak bu numara; hedef tasarım da onu başlığın
              hemen altına, rozetin soluna koyuyor. Bizde hiç görünmüyordu — künye kartının
              içinde, üstelik boşsa "Eksik" diye.

              ⚠ Numarası olmayan kayıtta hiç çizilmiyor: boş bir alanın yerini tutmak
              ("—", "Eksik") kimlik satırında gürültü olurdu; eksikliği künye kartı zaten
              söylüyor.
            */}
            {s.referenceNumber ? (
              <span data-testid="sozlesme-numarasi" className="text-sm text-fg-soft">
                {s.referenceNumber}
              </span>
            ) : null}
            {/*
              ⚠ "Düzenleme N" BURADAN KALKTI, silinmedi — Özet'teki künye kartına,
              "Temel bilgileri düzenle" düğmesinin yanına indi.

              Sayı hâlâ yararlı (eşzamanlı düzenlemede hangi hâli gördüğünüzü söyleyen tek
              şey o) ama kimlik satırının işi değil: kullanıcı kaydı numarasıyla ve
              durumuyla tanıyor, eşzamanlılık sayacıyla değil. Kimlik satırında durduğu
              sürece hem yer yiyor hem de "sürüm" sanılıyordu — 4 Eylül turunda adı bu
              yüzden değişmişti; asıl sorun adı değil YERİYDİ.
            */}
            {yasam.data ? (
              <ContractLifecyclePill states={yasam.data.states} status={yasam.data.status} />
            ) : null}
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/*
              PAYLAŞIM BAŞLIĞA TAŞINDI.

              ⚠ Eskiden bölüm çiplerinin arasındaydı ("Sözleşme metnini aç · Versiyon
              geçmişi · Revizyon önerileri · Paylaşım") ve o şerit kaldırıldı. Paylaşım
              diğer üçüyle aynı cinsten DEĞİLDİ: üçü bir bölüme GİDİYOR, bu bir pencere
              AÇIYOR. Kaydın tamamına ait bir eylem olduğu için başlık hizasında duruyor.

              ⚠ Düğme YETKİYE bağlı: paylaşamayacak kullanıcıyı 403 duvarına
              toslatmıyoruz. Kayıt erişimi kapısı yine sunucuda.

              ⚠ BU YÜZEY KAYIT DÜZEYİNE BAĞLANMADI ve sebebi ölçüm (CLOS-888): paylaşım
              uçlarının YAZANLARI `requireRecordWriteAccess` taşıyor, ama açılan pencere
              MEVCUT paylaşımları da GÖSTERİYOR. Düğmeyi `kayitYazma` ile kapatmak, yazma
              yüzeyiyle birlikte okuma görünürlüğünü de kapatırdı — kullanıcı
              sözleşmesinin kiminle paylaşıldığını göremez olurdu.
            */}
            {can("contract.share") ? (
              <Button variant="secondary" size="sm" onClick={() => setPaylasimAcik(true)}>
                Sözleşme Erişimi
              </Button>
            ) : null}
            {/*
              EYLEM HİYERARŞİSİ — birincil "sonraki adım", gerisi `⋯` içinde.

              ⚠ ÖLÇÜLEN KUSUR: sayfanın en görünür yeri, en nadir ve en tehlikeli üç
              eylemi taşıyordu. Aktif bir sözleşmede başlıkta yan yana "Sona Erdi",
              "Feshedildi", "Arşiv" duruyordu; üçü de aynı ağırlıkta, ikisi geri
              alınamaz ve hiçbiri kullanıcının o an yapmak istediği şey değil. Hedef
              tasarımın aynı yerinde "Paylaş" ve bir `⋯` var.

              ⚠ "SONRAKİ ADIM" UYDURULMUYOR, TÜRETİLİYOR. Sunucu hem kanonik durum
              sırasını (`states`) hem uygulanabilir geçişleri (`availableTransitions`)
              veriyor; ileri adım, sıradaki durumun geçişler arasında bulunmasıyla
              belirleniyor. Sunucu o geçişi sunmuyorsa birincil düğme de YOK — ekran
              kendi akışını kurmuyor.

              ⚠ Düğme metni yine SUNUCUDAN gelen hedef durum adı (refleks 4).
            */}
            {ileriGecis ? (
              <Button
                type="button"
                loading={gecis.isPending}
                onClick={() => gecis.mutate({ to: ileriGecis.to })}
              >
                {ileriGecis.label}
              </Button>
            ) : null}
            {/*
              ⚠ DURUM AKSİYONLARI kayıt düzeyine bağlı (CLOS-888): durum değiştirmek bir
              yazma işlemi ve sunucudaki kapısı `requireRecordWriteAccess`. Sunucunun
              verdiği geçiş listesi "bu SÖZLEŞME hangi duruma geçebilir" sorusunu
              cevaplıyor, "bu KULLANICI geçirebilir mi" sorusunu değil.

              ⚠ Menü yalnız GERİ KALAN geçişler için: ileri adım zaten birincil düğmede.
              Hiç kalmadıysa `⋯` de çizilmiyor — boş bir menü tetikleyicisi, arkasında bir
              şey varmış izlenimi verirdi.
            */}
            {kayitYazma && menuGecisleri.length > 0 ? (
              <Popover
                open={menuAcik}
                onClose={() => setMenuAcik(false)}
                align="right"
                trigger={
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label="Diğer durum aksiyonları"
                    aria-expanded={menuAcik}
                    onClick={() => setMenuAcik((a) => !a)}
                  >
                    ⋯
                  </Button>
                }
              >
                {/*
                  ⚠ `role="menu"` / `role="menuitem"` BİLEREK YOK.

                  İlk yazımda vardı ve TUTULMAYAN BİR SÖZDÜ: `role="menu"` ekran
                  okuyucuyu odak (uygulama) kipine sokar, kullanıcı ok tuşuna basar ve
                  hiçbir şey olmaz — ne ok tuşu, ne Home/End, ne roving tabindex
                  yazılmıştı. Rol koymamış olmaktan DAHA KÖTÜ bir durum: rol yokken
                  kullanıcı düğme listesi bekler ve Tab'la gezer, o beklenti karşılanır.

                  Kural bu kod tabanında zaten yazılı — `packages/ui/src/Tabs.tsx`:
                  *"yardımcı teknolojiye tutulmayacak bir söz verme"* (orada
                  `role="tablist"` aynı gerekçeyle reddedilmiş). Burada o kuralı
                  uyguluyoruz.

                  ⚠ Ok tuşu davranışı YAZILMADI, rol KALDIRILDI. İki çağıran var
                  (bu menü ve `layouts/ProfileMenu`) ve ProfileMenu'nün öğelerinin çoğu
                  komut değil GEZİNME — `role="menuitem"` orada zaten yanlış olurdu.
                  Üçüncü bir gerçek komut menüsü çıkarsa karar yeniden değerlendirilir.
                */}
                <ul aria-label="Durum aksiyonları" className="flex w-56 flex-col p-1">
                  {menuGecisleri.map((g) => (
                    /*
                      ⚠ KİT DÜĞMESİ, ham HTML düğme elementi DEĞİL (refleks 2 ·
                      `tools/uiGuards` Bekçi 3). İlk yazımda burada ham bir düğme elementi
                      vardı ve bekçi onu `bun run check`te yakaladı: `pages/**` altında ham
                      etkileşimli element yasak.

                      ⚠ Bekçi METİN tarıyor, AST değil: yorumun içinde geçen bir etiket
                      adı da ihlal sayılıyor. İlk düzeltmede tam bunu yaptım ve bekçi
                      haklı olarak iki ihlal daha saydı — bu yüzden etiket adı burada
                      düz metinle yazılı. `Button` `ButtonHTMLAttributes`'ı genişlettiği için
                      `role` ve `className` doğrudan geçiyor — menü öğesi için yeni bir
                      kit bileşeni açmak gerekmedi.

                      ⚠ `packages/ui`'ye `MenuItem` AÇILMADI: bu desenin ikinci çağıranı
                      `layouts/ProfileMenu` ve o kendi ham düğmelerini kullanıyor (kabuk
                      katmanı bekçinin kapsamı dışında). İki çağıran için kit bileşeni
                      açmak, CLOS-834'te `IconButton`'ın aynı gerekçeyle silinmesiyle
                      çelişirdi; üçüncü çağıran gelirse karar değişir.
                    */
                    <li key={g.to}>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={gecis.isPending}
                        className="w-full justify-start"
                        /**
                         * ⚠ ARŞİVLEME tek tıkla olmuyor (CLOS-122 `[104]`): önce etkiler ve
                         * açık işler gösteriliyor. Diğer geçişler doğrudan uygulanıyor —
                         * arşiv, kaydı akıştan çıkardığı için ayrı bir onay hak ediyor.
                         */
                        onClick={() => {
                          setMenuAcik(false);
                          if (g.to === "archived") setArsivAcik(true);
                          else gecis.mutate({ to: g.to });
                        }}
                      >
                        {g.label}
                      </Button>
                    </li>
                  ))}
                </ul>
              </Popover>
            ) : null}
          </div>
        }
      />

      {/*
        BÖLÜM GEZİNMESİ — artık GERÇEK ROTALAR.

        ⚠ Eskiden burada iki şerit vardı: `Tabs` (state değiştiren düğmeler) ve hemen
        altında dört `<Link>` çipi (rota değiştiren bağlantılar). İkisi aynı görsel
        ağırlıktaydı ama farklı davranıyordu — kullanıcı hangisinin sayfayı değiştireceğini
        tıklamadan bilemiyordu. Tek şerit kaldı ve hepsi rota; "Sözleşme metnini aç",
        "Versiyon geçmişi", "Revizyon önerileri" çipleri Belge bölümüne, "Paylaşım" başlık
        eylemine taşındı.
      */}
      {/*
        ⚠ ŞERİT YAPIŞKAN. Bölümlerin bazısı uzun (Kayıt, Taraflar) ve kaydırınca hem
        gezinme hem durum ekrandan çıkıyordu: kullanıcı başka bölüme geçmek için önce
        yukarı kaydırmak zorundaydı. `top-0` üst barın ALTINA değil sayfanın kendi kaydırma
        kabına yapışıyor — üst bar zaten kendi katmanında sabit.
      */}
      <Tabs
        className="sticky top-0 z-10 -mx-1 bg-canvas px-1"
        label="Sözleşme bölümleri"
        items={bolumler.map((bolum) => ({ key: bolum.key, label: bolum.label }))}
        activeKey={aktifBolum}
        onSelect={(key) => {
          const hedef = bolumler.find((bolum) => bolum.key === key);
          if (!hedef) return;
          /** Geliş bağlamı bölümler arasında da KORUNUYOR: listeye dönüş yolu kaybolmasın. */
          void navigate(
            `${contractDetailSectionPath(id, hedef.path)}${
              listeSorgusu ? `?liste=${encodeURIComponent(listeSorgusu)}` : ""
            }`,
          );
        }}
      />

      <Outlet context={bolumBaglami} />

      {/*
        Arşivden geri alma (CLOS-123 `[105]`) — düğme yalnız arşivdeki sözleşmede ve geri
        dönülecek bir durum varsa çıkıyor; hedefi geçmiş söylüyor.
      */}
      {kayitYazma && yasam.data?.restoreTo && (
        <p>
          <Button type="button" variant="secondary" onClick={() => setGeriAlAcik(true)}>
            Arşivden geri al
          </Button>
        </p>
      )}

      <ContractAccessDialog
        contractId={s.id}
        open={paylasimAcik}
        onClose={() => setPaylasimAcik(false)}
        arsivde={arsivde}
      />

      <ContractRestoreDialog
        contractId={s.id}
        open={geriAlAcik}
        onClose={() => setGeriAlAcik(false)}
        isPending={geriAl.isPending}
        onConfirm={(girdi) =>
          geriAl.mutate({
            reason: girdi.reason || undefined,
            acknowledgeWarnings: girdi.acknowledgeWarnings,
          })
        }
      />

      <ContractArchiveDialog
        contractId={s.id}
        open={arsivAcik}
        onClose={() => setArsivAcik(false)}
        isPending={gecis.isPending}
        onConfirm={(girdi) =>
          gecis.mutate({
            to: "archived",
            reason: girdi.reason || undefined,
            acknowledgeWarnings: girdi.acknowledgeWarnings,
          })
        }
      />

      <Link className="text-sm font-medium text-primary hover:underline" to={listeYolu}>
        Listeye dön
      </Link>
    </div>
  );
}
